<template>
  <MRow :gutter="28">
    <MCol>
      <MRow :gutter="0">
        <MCol
          class="border-neutral-lighter border-b border-solid border-t-0 border-r-0 border-l-0"
        >
          <h6 class="text-primary font-normal">{{ $t('other_info') }}</h6>
        </MCol>
      </MRow>
      <slot name="before"></slot>
      <MRow class="my-3">
        <MCol>
          <div>
            <small class="text-neutral-light">
              {{ $tc('gl_code') }}
            </small>
          </div>
          <div>
            <GlCodePicker
              :value="value.glCode"
              :class="{ 'read-only': disabled }"
              input-classes="material-input"
              :disabled="disabled"
              :placeholder="disabled ? '---' : undefined"
              as-input
              @change="handleChange({ glCode: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow class="my-3">
        <MCol
          class="border-neutral-lighter border-b border-solid border-t-0 border-r-0 border-l-0 py-1"
        >
          <div>
            <small class="text-neutral-light">
              {{ `${$tc('print')} ${$tc('template')}` }}
            </small>
          </div>
          <div v-if="value.printTemplateId">
            <PrintTemplatePicker
              v-model="value.printTemplateId"
              disabled
              text-only
              :placeholder="'---'"
              :module-name="$constants.PURCHASE"
            />
          </div>
          <span v-else>---</span>
        </MCol>
      </MRow>
      <MRow class="my-3">
        <MCol>
          <div>
            <small class="text-neutral-light">
              {{ $tc('invoice') }} {{ $tc('received') }}
            </small>
          </div>
          <div>
            <FlotoDropdownPicker
              v-model="value.invoiceReceived"
              :options="invoiceOptions"
              :class="{ 'read-only': isInvoiceAndPaymentDisabled }"
              input-classes="material-input"
              :disabled="isInvoiceAndPaymentDisabled"
              :placeholder="isInvoiceAndPaymentDisabled ? '---' : undefined"
              as-input
              @change="
                handleChange({
                  invoiceReceived: $event || ($event === false ? false : null),
                })
              "
            />
          </div>
        </MCol>
      </MRow>
      <MRow class="my-3">
        <MCol>
          <div>
            <small class="text-neutral-light">
              {{ $tc('payment') }} {{ $tc('status') }}
            </small>
          </div>
          <div>
            <FlotoDropdownPicker
              :value="value.paymentStatus"
              :options="paymentOptions"
              :class="{ 'read-only': isInvoiceAndPaymentDisabled }"
              input-classes="material-input"
              :disabled="isInvoiceAndPaymentDisabled"
              :placeholder="isInvoiceAndPaymentDisabled ? '---' : undefined"
              as-input
              :allow-clear="false"
              @change="handleChange({ paymentStatus: $event })"
            />
          </div>
        </MCol>
      </MRow>
      <MRow class="my-3">
        <MCol
          class="border-neutral-lighter border-b border-solid border-t-0 border-r-0 border-l-0"
        >
          <div>
            <small class="text-neutral-light">
              {{ $tc('total') }} {{ $tc('invoice') }} {{ $tc('amount') }}
            </small>
          </div>
          <div class="mt-2 mb-1">
            <div v-if="value.totalInvoiceAmount" class="flex">
              {{ `${value.totalInvoiceAmount.toFixed(2)}` }}
              <CurrencyPicker :value="value.currencyId" text-only disabled />
            </div>
            <div v-else> --- </div>
          </div>
        </MCol>
      </MRow>
      <MRow class="my-3">
        <MCol
          class="border-neutral-lighter border-b border-solid border-t-0 border-r-0 border-l-0"
        >
          <div>
            <small class="text-neutral-light">
              {{ $tc('total') }} {{ $tc('payment') }} {{ $tc('amount') }}
            </small>
          </div>
          <div class="mt-2 mb-1">
            <div v-if="value.totalPaymentAmount" class="flex">
              {{ `${value.totalPaymentAmount.toFixed(2)}` }}
              <CurrencyPicker :value="value.currencyId" text-only disabled />
            </div>
            <div v-else> --- </div>
          </div>
        </MCol>
      </MRow>
      <MRow class="my-3">
        <MCol
          class="border-neutral-lighter border-b border-solid border-t-0 border-r-0 border-l-0"
        >
          <div>
            <small class="text-neutral-light">
              {{ $t('last_updated_date') }}
            </small>
          </div>
          <div v-if="value.updatedTime" class="mt-2">
            {{ value.updatedTime | datetime }}
          </div>
          <div v-if="value.updatedTime" class="mb-1">
            ({{ value.updatedTime | timeago }})
          </div>
          <div v-else class="mt-2 mb-1">{{ '---' }}</div>
        </MCol>
      </MRow>
      <MRow class="my-3">
        <MCol
          class="border-neutral-lighter border-b border-solid border-t-0 border-r-0 border-l-0"
        >
          <div>
            <small class="text-neutral-light">
              {{ $tc('received') }} {{ $tc('date') }}
            </small>
          </div>
          <div v-if="value.receivedDate" class="mt-2 mb-1">
            {{ value.receivedDate | datetime }}
          </div>
          <div v-else class="mt-2 mb-1">{{ '---' }}</div>
        </MCol>
      </MRow>
      <MRow class="my-3">
        <MCol
          class="border-neutral-lighter border-b border-solid border-t-0 border-r-0 border-l-0"
        >
          <div>
            <small class="text-neutral-light">
              {{ $tc('purchase') }} {{ $tc('close') }} {{ $tc('date') }}
            </small>
          </div>
          <div v-if="value.purchaseCloseDate" class="mt-2 mb-1">
            {{ value.purchaseCloseDate | datetime }}
          </div>
          <div v-else class="mt-2 mb-1">{{ '---' }}</div>
        </MCol>
      </MRow>
    </MCol>
  </MRow>
</template>

<script>
import CurrencyPicker from '@components/data-picker/currency-picker'
import { dropdownTypeMap } from '@data/qualification'
import { OrganizationComputed } from '@state/modules/organization'
import { stageConstants } from '@utils/status'
import GlCodePicker from '@components/data-picker/gl-code-picker'
import PrintTemplatePicker from '@components/data-picker/print-template-picker'
import { authComputed } from '@state/modules/auth'
import { getPurchaseStatusSystemName } from '../../helpers/status'
export default {
  name: 'Sidebar',
  components: { GlCodePicker, CurrencyPicker, PrintTemplatePicker },
  model: {
    event: 'change',
  },
  props: {
    value: { type: Object, required: true },
    moduleName: { type: String, required: true },
    disabled: { type: Boolean, default: false },
    allowedFields: { type: Array, default: undefined },
    changeStage: { type: String, default: undefined },
    purchaseStatus: { type: String, required: true },
  },
  data() {
    this.statusName = getPurchaseStatusSystemName
    this.stageConstants = stageConstants
    this.invoiceOptions = dropdownTypeMap.invoiceStatus().options
    this.paymentOptions = dropdownTypeMap.paymentStatus().options
    return {}
  },
  computed: {
    ...authComputed,
    ...OrganizationComputed,
    isInvoiceAndPaymentDisabled() {
      return (
        this.disabled ||
        [
          this.statusName['ordered'],
          this.statusName['partially_received'],
          this.statusName['received'],
        ].indexOf(this.purchaseStatus) === -1
      )
    },
  },
  methods: {
    handleChange(change) {
      this.$emit('change', change)
    },
  },
}
</script>

<style lang="less" scoped>
.bordered-bottom {
  @apply border-solid border-neutral-lighter border-b border-t-0 border-l-0 border-r-0;
}
</style>
